/**
 * Coordinates the short-lived LA AI -> POE inbox bridge.
 * Standalone POE keeps its normal session-based auth path.
 */
import { useEffect, useState } from 'react';
import { applyShellTheme, asResolvedTheme } from '../lib/theme';
import {
  INBOX_BRIDGE_APP_SLUG,
  INBOX_BRIDGE_TOKEN_KEY,
  INBOX_BRIDGE_VERSION,
} from '../lib/embedBridge.js';

export interface EmbeddedModeState {
  isEmbedded: boolean;
  parentOrigin: string | null;
  isStandalone: boolean;
  bridgeToken: string | null;
}

function isHttpOrigin(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function readBridgeToken(): string | null {
  try {
    return window.sessionStorage.getItem(INBOX_BRIDGE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeBridgeToken(token: string): void {
  try {
    window.sessionStorage.setItem(INBOX_BRIDGE_TOKEN_KEY, token);
  } catch {
    // A restricted storage context should not prevent the standalone app from loading.
  }
}

export function useEmbeddedMode(): EmbeddedModeState {
  const [state, setState] = useState<EmbeddedModeState>(() => {
    if (typeof window === 'undefined') {
      return { isEmbedded: false, parentOrigin: null, isStandalone: true, bridgeToken: null };
    }
    return {
      isEmbedded: false,
      parentOrigin: null,
      isStandalone: window.parent === window,
      bridgeToken: readBridgeToken(),
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;

    let parentOrigin: string | null = null;
    let readyAttempts = 0;

    const sendReady = () => {
      try {
        window.parent.postMessage({ type: 'shell:poe-ready' }, '*');
      } catch {
        // The parent may be navigating away.
      }
    };

    const requestTokenRefresh = () => {
      if (!parentOrigin) return;
      window.parent.postMessage(
        {
          type: 'CHILD_TOKEN_REFRESH_REQUEST',
          version: INBOX_BRIDGE_VERSION,
          appSlug: INBOX_BRIDGE_APP_SLUG,
        },
        parentOrigin,
      );
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as {
        type?: string;
        origin?: unknown;
        token?: unknown;
        version?: unknown;
        appSlug?: unknown;
      } | undefined;

      if (data?.type === 'shell:hello') {
        if (!isHttpOrigin(event.origin) || data.origin !== event.origin) return;
        parentOrigin = event.origin;
        window.parent.postMessage(
          {
            type: 'CHILD_READY',
            version: INBOX_BRIDGE_VERSION,
            appSlug: INBOX_BRIDGE_APP_SLUG,
          },
          parentOrigin,
        );
        return;
      }

      // The shell's theme wins while embedded, so POE does not render light
      // inside a dark parent. Accepted only from the handshaked parent origin,
      // and never written to localStorage - see applyShellTheme.
      if (data?.type === 'shell:theme' && parentOrigin && event.origin === parentOrigin) {
        const resolved = asResolvedTheme((data as { theme?: unknown }).theme);
        if (resolved) applyShellTheme(resolved);
        return;
      }

      if (
        (data?.type === 'SHELL_AUTH' || data?.type === 'SHELL_TOKEN_REFRESH') &&
        parentOrigin &&
        event.origin === parentOrigin &&
        data.version === INBOX_BRIDGE_VERSION &&
        data.appSlug === INBOX_BRIDGE_APP_SLUG &&
        typeof data.token === 'string' &&
        data.token.length > 0
      ) {
        storeBridgeToken(data.token);
        setState({
          isEmbedded: true,
          parentOrigin,
          isStandalone: false,
          bridgeToken: data.token,
        });
      }
    };

    window.addEventListener('message', onMessage);
    sendReady();
    const readyTimer = window.setInterval(() => {
      if (parentOrigin || readyAttempts >= 10) {
        window.clearInterval(readyTimer);
        return;
      }
      readyAttempts += 1;
      sendReady();
    }, 500);

    const refreshTimer = window.setInterval(requestTokenRefresh, 10 * 60 * 1000);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(readyTimer);
      window.clearInterval(refreshTimer);
    };
  }, []);

  return state;
}
